/**
 * Background-noise removal for the phone-call mic, using RNNoise (an
 * open-source neural noise suppressor) running in the browser. Audio never
 * leaves the user's machine, so no vendor or BAA is involved.
 *
 * Plugged into the Twilio Voice SDK through its Audio Processor API
 * (`device.audio.addProcessor`): the SDK hands us the mic stream and sends
 * whatever stream we return.
 *
 * On by default for everyone; each user can turn it off in Phone Settings
 * ("Reduce background noise").
 */
import rnnoiseWorkletUrl from '@sapphi-red/web-noise-suppressor/rnnoiseWorklet.js?url'
import rnnoiseWasmUrl from '@sapphi-red/web-noise-suppressor/rnnoise.wasm?url'
import rnnoiseSimdWasmUrl from '@sapphi-red/web-noise-suppressor/rnnoise_simd.wasm?url'

import { loadDevicePrefs, updateDevicePrefs } from '@/services/devicePrefs'

// RNNoise is trained on 48 kHz audio.
const SAMPLE_RATE = 48000

let libraryPromise = null

// Loaded on first use: the package subclasses AudioWorkletNode at import time,
// which throws in browsers without it, and it shouldn't weigh on page load.
function loadLibrary() {
  if (!libraryPromise) {
    libraryPromise = (async () => {
      const { loadRnnoise, RnnoiseWorkletNode } = await import('@sapphi-red/web-noise-suppressor')
      const wasmBinary = await loadRnnoise({ url: rnnoiseWasmUrl, simdUrl: rnnoiseSimdWasmUrl })
      return { RnnoiseWorkletNode, wasmBinary }
    })().catch((e) => {
      libraryPromise = null
      throw e
    })
  }
  return libraryPromise
}

/** False in browsers without AudioWorklet; calls then use the browser's own suppression. */
export function isNoiseSuppressionAvailable() {
  return typeof AudioWorkletNode !== 'undefined'
}

/** The user's choice; defaults to on. */
export function isNoiseSuppressionPreferred() {
  return loadDevicePrefs().noiseSuppression
}

export function setNoiseSuppressionPreferred(enabled) {
  updateDevicePrefs({ noiseSuppression: enabled })
}

export function shouldUseNoiseSuppression() {
  return isNoiseSuppressionAvailable() && isNoiseSuppressionPreferred()
}

/** Implements the Twilio Voice SDK's AudioProcessor interface. */
export class RnnoiseAudioProcessor {
  constructor() {
    this._graphs = new Map()
  }

  async createProcessedStream(stream) {
    const { RnnoiseWorkletNode, wasmBinary } = await loadLibrary()
    const context = new AudioContext({ sampleRate: SAMPLE_RATE })
    await context.audioWorklet.addModule(rnnoiseWorkletUrl)
    const source = context.createMediaStreamSource(stream)
    const rnnoise = new RnnoiseWorkletNode(context, { maxChannels: 1, wasmBinary })
    const destination = context.createMediaStreamDestination()
    source.connect(rnnoise)
    rnnoise.connect(destination)
    this._graphs.set(destination.stream, { context, source, rnnoise })
    // When a mic is unplugged mid-call the SDK opens the next one and asks for
    // a new stream, but never destroys the old one. Close a graph as soon as
    // its mic stops, or each unplug leaves an AudioContext running.
    stream.getAudioTracks().forEach((track) => {
      track.addEventListener('ended', () => this.destroyProcessedStream(destination.stream), { once: true })
    })
    return destination.stream
  }

  async destroyProcessedStream(stream) {
    const graph = this._graphs.get(stream)
    if (!graph) return
    this._graphs.delete(stream)
    graph.source.disconnect()
    graph.rnnoise.destroy()
    graph.rnnoise.disconnect()
    if (graph.context.state !== 'closed') await graph.context.close()
  }
}
